import * as React from "react"
import { format, parse, isValid } from "date-fns"
import { de } from "date-fns/locale"
import { Calendar as CalendarIcon } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

export function DatePicker({ value, onChange, placeholder = "TT.MM.JJJJ", className, ...props }) {
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState("")
  
  // Convert YYYY-MM-DD to DD.MM.YYYY for display
  React.useEffect(() => {
    if (value) {
      const dateObj = new Date(value + "T00:00:00")
      if (isValid(dateObj)) {
        setInputValue(format(dateObj, "dd.MM.yyyy"))
      }
    } else {
      setInputValue("")
    }
  }, [value])
  
  // Convert string date to Date object for calendar
  const dateValue = value ? new Date(value + "T00:00:00") : undefined
  
  const handleCalendarSelect = (date) => {
    if (date) {
      // Format as YYYY-MM-DD for the backend
      const formattedDate = format(date, "yyyy-MM-dd")
      onChange(formattedDate)
      setOpen(false)
    }
  }
  
  const handleInputChange = (e) => {
    const newValue = e.target.value
    setInputValue(newValue)
    
    // Auto-format: add dots after day and month
    if (newValue.length === 2 && !newValue.includes('.')) {
      setInputValue(newValue + '.')
    } else if (newValue.length === 5 && newValue.charAt(2) === '.' && !newValue.substring(3).includes('.')) {
      setInputValue(newValue + '.')
    }
  }
  
  const handleInputBlur = () => {
    // Try to parse the input as DD.MM.YYYY
    if (inputValue) {
      const parsed = parse(inputValue, "dd.MM.yyyy", new Date())
      if (isValid(parsed)) {
        const formattedDate = format(parsed, "yyyy-MM-dd")
        onChange(formattedDate)
      } else {
        // Reset to last valid value or empty
        if (value) {
          const dateObj = new Date(value + "T00:00:00")
          setInputValue(format(dateObj, "dd.MM.yyyy"))
        } else {
          setInputValue("")
        }
      }
    } else {
      onChange("")
    }
  }
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleInputBlur()
    }
  }

  return (
    <div className={cn("relative flex", className)}>
      <Input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="pr-10"
        maxLength={10}
        data-testid={props['data-testid']}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
            type="button"
          >
            <CalendarIcon className="h-4 w-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={dateValue}
            onSelect={handleCalendarSelect}
            locale={de}
            initialFocus
            captionLayout="dropdown-buttons"
            fromYear={1920}
            toYear={2030}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
